import { useState, useEffect } from 'react';
import { fetchRecentSales } from '../data/machinesApi';

// Module-level so a prefetch fired from the alerts page is ready when the drawer mounts.
const CACHE_TTL_MS = 60 * 1000;
const cache = new Map();

const cacheKey = (clientId, storeId, productId) => `${clientId}:${storeId}:${productId || 'all'}`;

const freshEntry = (key) => {
  const entry = cache.get(key);
  if (!entry || Date.now() - entry.at > CACHE_TTL_MS) return null;
  return entry;
};

export const prefetchRecentSales = (clientId, storeId, productId) => {
  if (!clientId || !storeId) return Promise.resolve(null);
  const key = cacheKey(clientId, String(storeId), productId ? String(productId) : null);
  const existing = freshEntry(key);
  if (existing) return existing.promise;

  const entry = { at: Date.now(), data: null };
  entry.promise = fetchRecentSales(clientId, storeId, productId)
    .then((data) => {
      entry.data = data;
      return data;
    })
    .catch((err) => {
      cache.delete(key);
      throw err;
    });
  cache.set(key, entry);
  return entry.promise;
};

const useRecentSales = (storeId, productId, clientId) => {
  const key = storeId && clientId ? cacheKey(clientId, String(storeId), productId ? String(productId) : null) : null;
  // State is tagged with the key it belongs to, so a changed key never shows the previous key's rows
  const [state, setState] = useState({ key: null, data: null, error: false });

  useEffect(() => {
    if (!key) return;

    const cached = freshEntry(key);
    if (cached?.data) {
      setState({ key, data: cached.data, error: false });
      return;
    }

    let cancelled = false;
    prefetchRecentSales(clientId, storeId, productId)
      .then((data) => {
        if (!cancelled) setState({ key, data, error: false });
      })
      .catch(() => {
        if (!cancelled) setState({ key, data: null, error: true });
      });

    return () => { cancelled = true; };
  }, [key, clientId, storeId, productId]);

  if (!key) return { data: null, loading: false, error: false };
  if (state.key === key) return { data: state.data, loading: false, error: state.error };

  // Key just changed: serve a cached result in this same render instead of waiting for the effect
  const cached = freshEntry(key);
  if (cached?.data) return { data: cached.data, loading: false, error: false };
  return { data: null, loading: true, error: false };
};

export default useRecentSales;
